import type { AudioRegenChoice } from "@/hooks/useTranslate";

const CHOICES: { value: AudioRegenChoice; label: string; title: string }[] = [
  { value: "now", label: "Regenerate now", title: "Re-synthesize narration from the new wording right away" },
  { value: "on_play", label: "On next play", title: "Re-synthesize the next time this passage is played" },
  { value: "skip", label: "Keep current", title: "Keep the existing narration; don't ask again for this wording" },
];

interface Props {
  onChoose: (choice: AudioRegenChoice) => void;
  // Highlights one option (deferred mode in the WordingSheet, where the choice
  // is applied together with the save rather than immediately).
  selected?: AudioRegenChoice | null;
  busy?: boolean;
  size?: "sm" | "md";
}

/**
 * Three-way control for stale narration: regenerate now / on next play / keep.
 * Presentational — the caller decides whether choosing fires a mutation
 * immediately (Reader) or is staged until save (WordingSheet).
 */
export function AudioRegenChoices({ onChoose, selected, busy, size = "md" }: Props) {
  const pad = size === "sm" ? "px-2 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs";
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {CHOICES.map((c) => {
        const active = selected === c.value;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onChoose(c.value)}
            disabled={busy}
            title={c.title}
            className={`rounded-full border font-medium transition-colors disabled:opacity-50 ${pad} ${
              active
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}
